import React, { useState, useEffect, useCallback } from "react";
import { ParkinsonsLogo, Phone, Web, Tw, Fb } from "images/svgs";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useWindowSize } from "hooks";

const Navbar = () => {
  const { windowWidth } = useWindowSize();
  const isMobile = windowWidth < 1024;

  const [navOpen, setNavOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = useCallback(() => {
    const scrollNavTop = window.scrollY;
    if (scrollNavTop >= 50) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  }, [setIsScrolled]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const toggleNav = () => {
    setNavOpen(!navOpen);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <Container scrolled={isScrolled} isMobile={isMobile}>
      <LogoButton type="button" onClick={scrollToTop}>
        <StyledLogo />
      </LogoButton>

      {(!isMobile || navOpen) && (
        <Links
          navOpen={navOpen}
          initial={isMobile ? { opacity: 0, x: 400 } : { opacity: 0, y: -50 }}
          animate={isMobile ? { opacity: 1, x: 0 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <CustomLink href="tel:01 8722234">
            <SVGContainer>
              <Phone />
            </SVGContainer>
            01 8722234
          </CustomLink>
          <CustomLink href="https://www.parkinsons.ie" target="blank">
            <SVGContainer>
              <Web />
            </SVGContainer>
            www.parkinsons.ie
          </CustomLink>
          <CustomLink href="https://www.facebook.com/parkinsons.ireland/">
            <SVGContainer>
              <Fb />
            </SVGContainer>
            parkinsons.ireland
          </CustomLink>
          <CustomLink href="https://twitter.com/parkinsonsire">
            <SVGContainer>
              <Tw />
            </SVGContainer>
            ParkinsonsIre
          </CustomLink>
          <CustomLink href="https://twitter.com/">
            <SVGContainer>
              <Tw />
            </SVGContainer>
            #TalkParkinsons
          </CustomLink>
        </Links>
      )}

      {isMobile && (
        <MenuToggle navOpen={navOpen} onClick={toggleNav}>
          <span />
        </MenuToggle>
      )}
    </Container>
  );
};

const SVGContainer = styled.div`
  width: 1.5rem;
  min-width: 1.5rem;
  height: 1.5rem;
  margin-right: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--abbvie-blue);
  padding: 0.3rem;
  svg {
    width: 100%;
    height: auto;
    max-height: 0.875rem;
  }
`;

const MenuToggle = styled.button`
  height: 60px;
  width: 60px;
  background: var(--abbvie-blue);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 0;
  justify-self: end;
  cursor: pointer;
  grid-area: 1 / 5 / span 1 / span 2;
  span {
    content: "";
    background: white;
    height: 3px;
    width: 20px;
    display: block;
    position: relative;
    transition: 0.5s;
    margin-top: -16px;
    transform: ${({ navOpen }) =>
      navOpen ? "translateY(8px) rotate(45deg)" : "none"};
    :before,
    :after {
      content: "";
      display: block;
      height: 3px;
      width: 20px;
      position: absolute;
      background: white;
      transition: 0.5s;
      transform-origin: center;
      opacity: 1;
    }
    :before {
      top: 8px;
      opacity: ${({ navOpen }) => (navOpen ? 0 : 1)};
    }
    :after {
      top: 16px;
      transform: ${({ navOpen }) =>
        navOpen ? "translateY(-16px) rotate(-90deg)" : "none"};
    }
  }
  @media (min-width: 1150px) {
    display: none;
  }
`;

const CustomLink = styled.a`
  font-weight: 700;
  font-size: 1.5rem;
  line-height: 1em;
  color: black;
  letter-spacing: 0.1rem;
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin: 0.25em 0;
  text-align: center;
  margin-bottom: 1em;
  white-space: nowrap;
  @media (min-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 0.5em 0;
  }
  @media (min-width: 1150px) {
    font-size: 0.75rem;
    padding-right: 2.5rem;
    height: 100%;
    width: auto;
    color: white;
    margin: 0;
    :first-child {
      border-top: none;
    }
    :nth-child(odd) {
      background: transparent;
    }
  }
  @media (min-width: 1600px) {
    font-size: 0.875rem;
  }
`;

const Links = styled(motion.div)`
  grid-area: 2 / 1 / span 1 / span 7;
  display: ${({ navOpen }) => (navOpen ? "flex" : "none")};
  flex-direction: column;
  height: auto;
  align-items: center;
  justify-content: flex-start;
  opacity: 0;
  height: calc(100vh - 60px);
  background: var(--abbvie-green);
  backdrop-filter: blur(2px);
  padding-top: 5rem;
  padding-bottom: 100px;
  @media (min-width: 1150px) {
    grid-area: 1 / 6 / span 1 / span 7;
    padding-top: 0;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    height: 100%;
    background: transparent;
    backdrop-filter: none;
    padding-bottom: 0;
  }
`;

const LogoButton = styled.button`
  height: 3rem;
  width: auto;
  border: none;
  outline: none;
  background: none;
  cursor: pointer;
  padding: 0.5rem;
  grid-area: 1 / 1 / span 1 / span 3;
  display: flex;
  @media (min-width: 1150px) {
    padding-left: 1rem;
    grid-area: 1 / 2 / span 1 / span 3;
  }
`;

const StyledLogo = styled(ParkinsonsLogo)`
  height: 100%;
  width: auto;
`;

const Container = styled.nav`
  width: 100%;
  height: 60px;
  max-width: 1440px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  align-items: center;
  justify-content: space-between;
  background: var(--abbvie-green);
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  margin: 0 auto;
  z-index: 4;
  box-shadow: 0px 5px 12px rgba(0, 0, 0, 0.15);
  @media (min-width: 768px) {
    margin: 0 auto;
  }
  @media (min-width: 1150px) {
    grid-template-columns: repeat(12, 1fr);
    height: ${({ isScrolled }) => (isScrolled ? "60px" : "80px")};
  }
`;

export default Navbar;
